import React, { useMemo, useRef, useState } from "react";
import './OrderedChannelsSelector.css';
import { Checkbox } from "@mui/material";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { IChannel } from "src/app/channel/~store/models/Channel";
import { GetChannelImage } from "src/app/shared/helpers/img-helpers/ImgHelper";

interface IOrderedSelectorProps {
  title: string;
  channels: IChannel[];
  selectedChannelsIds: number[];
  setSelectedChannelsIds: (items: number[]) => void;
}


const OrderedChannelsSelector = (props: IOrderedSelectorProps) => {

  const selectedChannelsIds = useMemo(() => props.selectedChannelsIds ?? [], [props.selectedChannelsIds]);
  const selectedChannels = useMemo(
    () => selectedChannelsIds.map(chId => props.channels.find(ch => ch.channelId === chId)),
    [ selectedChannelsIds]
  );
  const sortedChannels = useMemo(
    () => [...props.channels].sort((a, b) => a.name.localeCompare(b.name)), 
    [props.channels]
  );

  const [draggingItemId, setDraggingItemId] = useState<number | null>(null);
  const lastSelectedRef = useRef<HTMLDivElement | null>(null);


  const _onItemClicked = (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>, channelId: number) => {
    event.stopPropagation();
    props.setSelectedChannelsIds(selectedChannelsIds.includes(channelId) ? 
      selectedChannelsIds.filter(si => si !== channelId) : 
      [...selectedChannelsIds, channelId])
  }

  const _moveUp = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, channelId: number) => {
    event.stopPropagation();
    const index = selectedChannelsIds.findIndex(si => si === channelId);
    if (index > 0) {
      const newItems = [...selectedChannelsIds];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      props.setSelectedChannelsIds(newItems);
    }
  }

  const _moveDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, channelId: number) => {
    event.stopPropagation();
    const index = selectedChannelsIds.findIndex(si => si === channelId);
    if (index < selectedChannelsIds.length - 1) {
      const newItems = [...selectedChannelsIds];
      [newItems[index + 1], newItems[index]] = [newItems[index], newItems[index + 1]];
      props.setSelectedChannelsIds(newItems);
    }
  }

  const _onDragStart = (event: React.DragEvent<HTMLDivElement>, channelId: number) => {
    const draggedItem = event.target as HTMLDivElement;
    const dragImageNode = draggedItem.querySelector(".list-item-name");
    event.dataTransfer.setDragImage(dragImageNode!, 0, 0);
    draggedItem.classList.add("dragging");

    setDraggingItemId(channelId);
  }

  const _onDragEnd = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const draggedItem = event.target as HTMLDivElement;
    draggedItem.classList.remove("dragging");

    setDraggingItemId(null);
  }

  const _onDragOver = (event: React.DragEvent<HTMLDivElement>, dragOverChannelId: number) => {
    event.preventDefault();

    //currentElement is the element being dragged over
    const dragOverElement = event.currentTarget as HTMLDivElement;
    const dragOverItemIndex = selectedChannelsIds.findIndex(si => si === dragOverChannelId);
    const draggingItemIndex = selectedChannelsIds.findIndex(si => si === draggingItemId);

    // No change if dragging over the same item
    if (draggingItemIndex !== -1 && draggingItemIndex === dragOverItemIndex || !draggingItemId) {
      return;
    };

    //if dragging over item that is already selected
    if (dragOverItemIndex !== -1)
    {
      const dragOverMiddle = dragOverElement.getBoundingClientRect().top + dragOverElement.offsetHeight / 2;
      //if cursor is above the middle of the item being dragged over, move it above the item being dragged over
      if (event.clientY < dragOverMiddle && (draggingItemIndex > dragOverItemIndex || draggingItemIndex === -1)) {
        const newItems = [...selectedChannelsIds];
        const prevItems = newItems.slice(0, dragOverItemIndex);
        const nextItems = newItems.slice(dragOverItemIndex).filter(chId => chId !== draggingItemId);
        props.setSelectedChannelsIds([...prevItems, draggingItemId, ...nextItems]);
      }
      //if cursor is bellow the middle of the item being dragged over, move it bellow the item being dragged over
      else if (event.clientY > dragOverMiddle && draggingItemIndex < dragOverItemIndex) {
        const newItems = [...selectedChannelsIds];
        const prevItems = newItems.slice(0, dragOverItemIndex + 1).filter(si => si !== draggingItemId);
        const nextItems = newItems.slice(dragOverItemIndex + 1);
        props.setSelectedChannelsIds([...prevItems, draggingItemId, ...nextItems]);
      }
    }
    //if dragging over item that is not selected
    else {
      //if below the middle of the last selected item, add it to the end of the list
      if (lastSelectedRef.current && event.clientY > lastSelectedRef.current.getBoundingClientRect().top + lastSelectedRef.current.offsetHeight / 2) {
        //if the item is already selected, move it to the end of the list
        if (draggingItemIndex !== -1) {
          {
            props.setSelectedChannelsIds([...selectedChannelsIds.filter(si => si !== draggingItemId), draggingItemId!]);
          }
        }
      }
      //if thre is no last selected item, add it to the beginning of the list
      else if (!lastSelectedRef.current)
      {
        props.setSelectedChannelsIds([dragOverChannelId, ...selectedChannelsIds]);
      }
    }

  }

  return (
      <div className="ordered-selector-container">
        <div className="list-container">
          <div className="title">{props.title}</div>
          <div className="all-items-list">
            {selectedChannels.filter(ch => !!ch).map((item, index) => (
              <div key={item.channelId} className="list-item selected"
                draggable={true}
                onDragStart={(event) => _onDragStart(event, item.channelId)}
                onDragEnd={(event) => _onDragEnd(event)}
                onDragOver={(event) => _onDragOver(event, item.channelId)}
                onClick={(event) => _onItemClicked(event, item.channelId)}
                ref={index === selectedChannelsIds.length - 1 ? lastSelectedRef : null}>
                <div className="list-item-order">{index + 1}.</div>
                <Checkbox color="success"
                  onClick={(event) => _onItemClicked(event, item.channelId)}
                  checked={true}/>
                <img className="list-item-image" src={GetChannelImage(item.type)}/>
                <div className="list-item-name">{item.name}</div>
                <div className="order-controls">
                  <div onClick={(event) => _moveUp(event, item.channelId)} className="order-control-up">
                    <ArrowDropUpIcon className={"order-control-icon " + (index === 0 && "disabled")}/>
                  </div>
                  <div onClick={(event) => _moveDown(event, item.channelId)} className="order-control-down">
                    <ArrowDropDownIcon className={"order-control-icon " + (index === selectedChannelsIds.length - 1 && "disabled")}/>
                  </div>
                </div>
              </div>))
            }
            {sortedChannels.filter(i => !selectedChannelsIds.includes(i.channelId)).map((item) => (
              <div key={item.channelId} className="list-item" onClick={(event) => _onItemClicked(event, item.channelId)}
                draggable={true}
                onDragStart={(event) => _onDragStart(event, item.channelId)}
                onDragEnd={(event) => _onDragEnd(event)}
                onDragOver={(event) => _onDragOver(event, item.channelId)}>
                <Checkbox color="success"
                  onClick={(event) => _onItemClicked(event, item.channelId)}
                  checked={false}/>
                <img className="list-item-image" src={GetChannelImage(item.type)}/>
                <div className="list-item-name">{item.name}</div>
              </div>))
            }
          </div>

        </div>
      </div>
  )
}

export default OrderedChannelsSelector;
